// React i hooks
import { memo, useCallback, useEffect, useState } from "react";

// Tipovi i konstante
import { LeasingDetailProps } from "./types";
import { LeasingDetailFormValues } from "services/api/calculations/leasing/types";
import { initialLeasingDetailForm } from "services/api/calculations/leasing/consts";
import { Errors } from "services/forms/validation/types";
import {
  CALCULATION_LEASING_CREATE_PAGE_TITLE,
  CALCULATION_LEASING_DETAIL_PAGE_TITLE,
  CALCULATION_LEASING_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// API servisi
import { getLeasingAPI } from "services/api/calculations/leasing/api";

// UI komponente
import { Loader } from "ui/components/Loader";
import { Button } from "ui/components/Button/Button";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";

// Lokalne komponente
import LeasingEditCard from "../../../LeasingEditCard/LeasingEditCard";

// Helperi
import { useTranslate } from "helpers";

const LeasingDetailCard: React.FC<LeasingDetailProps> = (props) => {
  const { leasingId } = props;
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<LeasingDetailFormValues>(
    initialLeasingDetailForm
  );

  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  useEffect(() => {
    if (leasingId) {
      fetchLeasing(leasingId);
    }
  }, [leasingId]);

  const fetchLeasing = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialLeasingDetailForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getLeasingAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);
    setIsLoading(false);

    setErrors({});
  };

  const handleEditSuccess = useCallback(() => {
    if (leasingId) fetchLeasing(leasingId);
    setShowEditModal(false);
  }, []);

  if (!leasingId) return <></>;

  return (
    <>
      <div className="  flex justify-center ">
        <div className="rounded-sm bg-white border max-w-lg w-full">
          <div className="divide-y divide-slate-200">
            <div className="flex justify-between items-center">
              <p className="text-sm font-semibold text-slate-600 px-4 py-3 uppercase">
                {t(CALCULATION_LEASING_DETAIL_PAGE_TITLE.text)}
              </p>
              <div className="flex items-center gap-2 px-3 my-3 ">
                <Button
                  size="small"
                  variant="primary"
                  children={t("admin.change")}
                  onClick={() => setShowEditModal(true)}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.leasing.detail.name")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.name}
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.leasing.detail.leasingtypename")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.leasingTypeName}
              </div>
            </div>
            <div className="px-4 py-2 min-h-[56px]">
              <div className="text-xs font-medium uppercase text-slate-600">
                {t("admin.calculation.leasing.detail.interestrate")}
              </div>
              <div className="text-sm text-slate-500 truncate">
                {formValues.interestRate} %
              </div>
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
          <ModalHeader
            title={
              leasingId
                ? CALCULATION_LEASING_EDIT_PAGE_TITLE.text
                : CALCULATION_LEASING_CREATE_PAGE_TITLE.text
            }
            onHide={() => setShowEditModal(false)}
          />
          <ModalBody>
            <LeasingEditCard
              leasingId={leasingId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(LeasingDetailCard);
